import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { AlertIcon, DateRangePicker, Segments, SparkIcon } from "@signet/ui";
import { api, money, parseNowRange, useMe, type NowCardData, type NowRange, type NowRangeKey, useSeenQuery } from "@signet/web-kit";
import { webModules } from "../../../../modules/registry.web.ts";

/* ──────────────────────────────────────────────────────────────────────────
   Now: the home screen for everyone.

   Laid out from the boss's mockup (2026-09-29). A range across the top,
   then a row of figures (visitors, clicks, revenue), then
   the modules' panels two to a row, then a row of what needs a person: the
   cards a module puts beside Needs attention on the left, Needs attention on
   the right. Figures and panels both follow the range.

   Above all of it, a module can put a strip that only shows when it has
   something urgent (`nowTop`): Expiries' "runs out in the next few days".

   Modules supply all of it: the figures (`nowStats`), the panels
   (`nowPanels`) and the cards under Needs attention (`nowCards`), each behind
   the permission it reads with, so somebody who cannot see revenue gets a
   shorter row rather than a card that says no. The range lives in the
   address, so a link to "Now, last 7 days" opens on it.
   ────────────────────────────────────────────────────────────────────────── */

type Usage = { rows: { day: string; cost: number }[]; totals: { costUsd: number } };

const RANGES: { value: NowRangeKey; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "7d", label: "7D" },
  { value: "30d", label: "30D" },
];

function greeting(now = new Date()) {
  const hour = now.getHours();
  return hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
}

export function NowPage() {
  const { me, can } = useMe();
  const navigate = useNavigate();
  const range = parseNowRange(useSearch({ strict: false }) as Record<string, unknown>);
  const setRange = (next: NowRange) =>
    navigate({ to: "/", search: (next.key === "today" ? {} : { range: next.key, from: next.from, to: next.to }) as never, replace: true });

  const cards = useSeenQuery({ queryKey: ["now"], queryFn: () => api<{ cards: NowCardData[] }>("/api/me/now"), refetchInterval: 60_000 });
  const proposals = useQuery({
    queryKey: ["assistant", "proposals", "pending"],
    queryFn: () => api<{ proposals: { id: string; summary: string }[] }>("/api/assistant/proposals?status=pending"),
    enabled: can("signet.assistant:view"),
  });
  const usage = useQuery({ queryKey: ["usage", 1], queryFn: () => api<Usage>("/api/admin/usage?days=1"), enabled: can("signet.usage:view") });

  const stats = webModules.flatMap((m) => m.nowStats ?? []).filter((s) => can(s.needs)).sort((a, b) => a.order - b.order);
  const tops = webModules.flatMap((m) => m.nowTop ?? []).filter((t) => can(t.needs)).sort((a, b) => a.order - b.order);
  const panels = webModules.flatMap((m) => m.nowPanels ?? []).filter((p) => can(p.needs)).sort((a, b) => a.order - b.order);
  const renderers = Object.assign({}, ...webModules.map((m) => m.nowCards ?? {})) as Record<string, React.ComponentType<{ card: NowCardData }>>;
  const sideKinds = new Set(webModules.flatMap((m) => m.nowSideCards ?? []));
  const allCards = (cards.data?.cards ?? []).filter((card) => renderers[card.kind]);
  const sideCards = allCards.filter((card) => sideKinds.has(card.kind));
  const moduleCards = allCards.filter((card) => !sideKinds.has(card.kind));
  const pending = proposals.data?.proposals ?? [];
  const spentToday = usage.data?.totals.costUsd ?? 0;
  const quiet = !cards.isLoading && moduleCards.length === 0 && pending.length === 0;

  return (
    <div className="page now signet-now">
      {tops.map((top) => <top.component key={top.id} />)}

      <div className="signet-now-head">
        <div>
          <h1>{greeting()}, {me?.user.name ?? me?.user.username}</h1>
          <p>Here's what's happening across the agency today.</p>
        </div>
        <div className="signet-now-range">
          <Segments label="Range" value={range.key} options={RANGES} onChange={(key) => setRange({ key })} />
          <DateRangePicker
            label="Pick dates"
            presets={false}
            defaultLabel="Dates"
            value={range.key === "custom" ? { from: range.from ?? null, to: range.to ?? null } : { from: null, to: null }}
            onChange={(picked) => picked.from && picked.to && setRange({ key: "custom", from: picked.from, to: picked.to })}
          />
        </div>
      </div>

      {stats.length ? (
        <div className="signet-now-stats">
          {stats.map((stat) => <stat.component key={stat.id} range={range} />)}
        </div>
      ) : null}

      {/* Two columns, read left to right, top to bottom (Jay, 2026-10-08): each module's panel
          takes half a row unless it is wide, then the attention row. */}
      {panels.length ? (
        <div className="signet-now-grid">
          {panels.map((panel) => (
            <div key={panel.id} className={`signet-now-slot${panel.wide ? " wide" : ""}`}>
              <panel.component range={range} />
            </div>
          ))}
        </div>
      ) : null}

      <div className={`signet-now-body${sideCards.length ? "" : " single"}`}>
        {sideCards.length ? (
          <div className="signet-now-side">
            {sideCards.map((card) => {
              const Card = renderers[card.kind]!;
              return <Card key={card.id} card={card} />;
            })}
          </div>
        ) : null}

        <div className="signet-now-attention">
          <h2 className="signet-now-attention-head">
            <AlertIcon size={13} /> Needs attention
            {moduleCards.length + (pending.length ? 1 : 0) ? <span className="signet-now-count">{moduleCards.length + (pending.length ? 1 : 0)}</span> : null}
          </h2>

          {pending.length ? (
            <section className="panel now-card attention">
              <div className="panel-head"><h2>Waiting for your approval</h2></div>
              <div className="panel-body">
                <ul className="now-list">{pending.slice(0, 5).map((p) => <li key={p.id}>{p.summary}</li>)}</ul>
                <p className="faint">Open the assistant (<SparkIcon size={11} />) to approve or reject {pending.length === 1 ? "it" : "them"}.</p>
              </div>
            </section>
          ) : null}

          {moduleCards.map((card) => {
            const Card = renderers[card.kind]!;
            return <Card key={card.id} card={card} />;
          })}

          {quiet ? <p className="signet-now-quiet">Nothing needs you right now.</p> : null}

          {spentToday > 0 ? (
            <section className="panel now-card">
              <div className="panel-head"><h2>AI spend today</h2></div>
              <div className="panel-body"><p className="now-figure tabular">{money(spentToday)}</p></div>
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
}
